import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@e2n/ui';
import { TerminKalender } from '@/domain/termine/shared/types/TerminKalender';
import { faTriangleExclamation } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

type KalenderDeleteDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  kalender: TerminKalender;
  otherKalender: TerminKalender[];
  onConfirm: (idTargetTerminkalender?: string) => void;
  loading?: boolean;
};

function TargetKalenderSelect({
  otherKalender,
  value,
  onChange,
}: {
  otherKalender: TerminKalender[];
  value?: string;
  onChange: (id: string) => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="px-4">
      <p className="text-sm text-neutrals-900 pb-3">
        {t('domain.termine.kalender.deleteDialog.targetLabel')}
      </p>
      <Select onValueChange={onChange} value={value}>
        <SelectTrigger className="border-neutrals-300 w-75">
          <SelectValue
            placeholder={t(
              'domain.termine.kalender.deleteDialog.targetPlaceholder',
            )}
          />
        </SelectTrigger>
        <SelectContent>
          <SelectGroup>
            {otherKalender.map((k) => (
              <SelectItem key={k.id} value={k.id}>
                {k.name}
              </SelectItem>
            ))}
          </SelectGroup>
        </SelectContent>
      </Select>
    </div>
  );
}

export function KalenderDeleteDialog({
  open,
  onOpenChange,
  kalender,
  otherKalender,
  onConfirm,
  loading,
}: KalenderDeleteDialogProps) {
  const { t } = useTranslation();
  const [targetId, setTargetId] = useState<string | undefined>(undefined);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="top-3/12 translate-y-0 p-4 max-w-none w-170">
        <DialogHeader className="*:text-neutrals-900 px-4">
          <DialogTitle className="py-4 text-xl">
            {t('domain.termine.kalender.deleteDialog.title')}
          </DialogTitle>
          <DialogDescription>
            {t('domain.termine.kalender.deleteDialog.description', {
              name: kalender.name,
            })}
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-2 pt-2">
          <TargetKalenderSelect
            otherKalender={otherKalender}
            value={targetId}
            onChange={setTargetId}
          />

          <div className="flex items-center px-4 gap-1 text-primary-orange-500 text-sm font-semibold">
            <FontAwesomeIcon icon={faTriangleExclamation} size="lg" />
            <span> {t('domain.termine.kalender.deleteDialog.warning')}</span>
          </div>
        </div>
        <DialogFooter className="flex flex-row gap-2 justify-end p-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t('domain.termine.deleteDialog.cancel')}
          </Button>
          <Button
            disabled={!targetId || loading}
            onClick={() => onConfirm(targetId)}>
            {t('domain.termine.form.fertig')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
